import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getCentralToday, getCentralMonth } from '@/utils/dateHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminContext = createContext(null);

// Helpers for month <-> (start,end) boundaries.
// Input `yyyyMm` e.g. "2026-05" -> ["2026-05-01","2026-05-31"]
const monthBounds = (yyyyMm) => {
  if (!yyyyMm || !/^\d{4}-\d{2}$/.test(yyyyMm)) return [null, null];
  const [y, m] = yyyyMm.split('-').map(Number);
  const last = new Date(y, m, 0).getDate();
  return [`${yyyyMm}-01`, `${yyyyMm}-${String(last).padStart(2, '0')}`];
};

// Build a list of recent months (this month + N previous) for the dropdown.
const buildRecentMonths = (count = 24) => {
  const now = getCentralMonth(); // "2026-05"
  const [y, m] = now.split('-').map(Number);
  const out = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(y, m - 1 - i, 1);
    const yy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    out.push(`${yy}-${mm}`);
  }
  return out;
};

export { API };

export function AdminProvider({ children }) {
  // Core data
  const [stats, setStats] = useState({});
  const [records, setRecords] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  // Default filter to the CURRENT MONTH (Central time) so tabs open showing this month only.
  // User can pick older months via the dropdown or pick a custom range via the date picker.
  const initialMonth = getCentralMonth();
  const [initialStart, initialEnd] = monthBounds(initialMonth);
  const [selectedMonth, setSelectedMonth] = useState(initialMonth);
  const [startDate, setStartDate] = useState(initialStart);
  const [endDate, setEndDate] = useState(initialEnd);
  const [isFiltered, setIsFiltered] = useState(true);
  // Per-tab date-field choice. Most tabs (Dashboard, Billing) default to "check_out_date"
  // so the totals align with the railroad billing summary. SignIn keeps "check_in_date"
  // (sign-in sheets are by definition signed at check-in).
  const [dateField, setDateField] = useState("check_out_date");

  // Recent-months list for the dropdown (24 months back).
  const [recentMonths] = useState(() => buildRecentMonths(24));

  // Sync status (shared by Dashboard and Settings)
  const [syncStatus, setSyncStatus] = useState(null);
  const [markingNoBill, setMarkingNoBill] = useState({});

  // Core fetch functions
  const fetchData = useCallback(async (filterStart = null, filterEnd = null, retryCount = 0, dateFieldArg = null) => {
    try {
      const effectiveField = dateFieldArg || dateField;

      let recordsUrl = `${API}/admin/records`;
      const recordsParams = new URLSearchParams();
      if (filterStart) recordsParams.append('start_date', filterStart);
      if (filterEnd)   recordsParams.append('end_date', filterEnd);
      if (effectiveField) recordsParams.append('date_field', effectiveField);
      if (recordsParams.toString()) recordsUrl += `?${recordsParams.toString()}`;

      // Stats — also month-scoped when a filter is set, so cards reflect the selected month.
      let statsUrl = `${API}/admin/stats`;
      const statsParams = new URLSearchParams();
      if (filterStart) statsParams.append('start_date', filterStart);
      if (filterEnd)   statsParams.append('end_date', filterEnd);
      if (effectiveField) statsParams.append('date_field', effectiveField);
      if (statsParams.toString()) statsUrl += `?${statsParams.toString()}`;

      const [statsRes, recordsRes] = await Promise.all([
        axios.get(statsUrl),
        axios.get(recordsUrl),
      ]);
      setStats(statsRes.data);
      setRecords(recordsRes.data);
    } catch (error) {
      if (retryCount < 1 && error.response?.status === 401) {
        await new Promise(resolve => setTimeout(resolve, 500));
        return fetchData(filterStart, filterEnd, retryCount + 1, dateFieldArg);
      }
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [dateField]);

  const fetchRooms = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/rooms`);
      setRooms(response.data);
    } catch (error) {
      console.error("Failed to load rooms");
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error("Failed to load employees");
    }
  }, []);

  // Date filter handlers
  const handleApplyFilter = useCallback(() => {
    if (startDate || endDate) {
      setIsFiltered(true);
      fetchData(startDate, endDate);
      toast.success("Filter applied");
    }
  }, [startDate, endDate, fetchData]);

  // When the user toggles between Check-In / Check-Out date, re-run the filter
  // (or refresh the unfiltered view) so the records table updates immediately.
  const handleDateFieldChange = useCallback((newField) => {
    setDateField(newField);
    if (startDate || endDate) {
      fetchData(startDate, endDate, 0, newField);
    } else {
      fetchData(null, null, 0, newField);
    }
  }, [startDate, endDate, fetchData]);

  const handleClearFilter = useCallback(() => {
    setStartDate("");
    setEndDate("");
    setSelectedMonth("");
    setIsFiltered(false);
    fetchData();
    toast.success("Filter cleared");
  }, [fetchData]);

  // Jump to a specific month via dropdown. Updates both the month selector,
  // the start/end date pickers, and re-fetches scoped to that month.
  const handleSelectMonth = useCallback((yyyyMm, dateFieldOverride = null) => {
    const [ms, me] = monthBounds(yyyyMm);
    if (!ms || !me) return;
    setSelectedMonth(yyyyMm);
    setStartDate(ms);
    setEndDate(me);
    setIsFiltered(true);
    fetchData(ms, me, 0, dateFieldOverride);
  }, [fetchData]);

  // Sync/billing handler (shared by Dashboard + Settings)
  const handleMarkNoBill = useCallback(async (entry, targetDate) => {
    const entryKey = `${entry.name}-${targetDate}`;
    setMarkingNoBill(prev => ({ ...prev, [entryKey]: true }));
    try {
      const token = sessionStorage.getItem("adminToken");
      await axios.post(`${API}/admin/sync/mark-no-bill`, {
        name: entry.name || entry.api_name,
        date: targetDate || syncStatus?.last_run?.split('T')[0] || getCentralToday(),
        reason: "Manual no-bill by admin"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Marking ${entry.name} as No Bill... Check portal to verify.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark as No Bill");
    } finally {
      setMarkingNoBill(prev => ({ ...prev, [entryKey]: false }));
    }
  }, [syncStatus]);

  const value = {
    API,
    // Core data
    stats, records, rooms, employees, loading,
    // Core fetch
    fetchData, fetchRooms, fetchEmployees,
    // Date filter
    startDate, endDate, isFiltered, setIsFiltered,
    setStartDate, setEndDate,
    handleApplyFilter, handleClearFilter,
    // Month selector
    selectedMonth, setSelectedMonth, recentMonths, handleSelectMonth,
    // Date field toggle
    dateField, setDateField, handleDateFieldChange,
    // Sync
    syncStatus, setSyncStatus,
    markingNoBill, setMarkingNoBill,
    handleMarkNoBill,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const ctx = useContext(AdminContext);
  if (!ctx) throw new Error('useAdmin must be used within AdminProvider');
  return ctx;
}
